import { useState } from 'react';
import { Calendar, ShieldCheck, Mail, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

function calculateAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

export function AgeGateModal() {
  const { profile, updateProfile } = useAuth();
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentalConsent, setParentalConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!profile || profile.date_of_birth) return null;

  const age = dateOfBirth ? calculateAge(dateOfBirth) : null;
  const isMinor = age !== null && age < 18;
  const isUnder13 = age !== null && age < 13;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!dateOfBirth) {
      setError('Date of birth is required');
      return;
    }

    if (age !== null && age < 0) {
      setError('Please enter a valid date of birth');
      return;
    }

    if (isUnder13 && (!parentEmail || !parentalConsent)) {
      setError('Parental consent and a parent/guardian email are required for users under 13');
      return;
    }

    setSaving(true);

    const { error: updateError } = await updateProfile({
      date_of_birth: dateOfBirth,
      is_minor: isMinor,
      parent_email: isUnder13 ? parentEmail : null,
      parental_consent_given: isUnder13 ? parentalConsent : false,
    });

    if (updateError) {
      setError(updateError.message);
    }

    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-8 h-8" />
            <h2 className="text-xl font-bold">Age Verification</h2>
          </div>
          <p className="text-teal-100 text-sm">
            To keep our community safe, we need to verify your age. This only takes a moment.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="age-gate-dob" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Date of Birth
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="age-gate-dob"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
                max={new Date().toISOString().split('T')[0]}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 transition-colors"
              />
            </div>
            {age !== null && age >= 0 && isMinor && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                {isUnder13 ? 'Parental consent required for users under 13' : 'Under 18 — some features may be restricted'}
              </p>
            )}
          </div>

          {isUnder13 && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl space-y-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Parental Consent Required</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    Users under 13 need a parent or guardian's consent (COPPA).
                  </p>
                </div>
              </div>
              <div>
                <label htmlFor="age-gate-parent-email" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Parent/Guardian Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="age-gate-parent-email"
                    type="email"
                    value={parentEmail}
                    onChange={(e) => setParentEmail(e.target.value)}
                    required
                    placeholder="parent@example.com"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 transition-colors"
                  />
                </div>
              </div>
              <div className="flex items-start">
                <input
                  id="age-gate-consent"
                  type="checkbox"
                  checked={parentalConsent}
                  onChange={(e) => setParentalConsent(e.target.checked)}
                  required
                  className="w-4 h-4 mt-1 accent-gray-900 cursor-pointer appearance-auto ring-2 ring-gray-900 ring-offset-1"
                />
                <label htmlFor="age-gate-consent" className="ml-2 text-sm text-gray-600 dark:text-slate-400">
                  I confirm that I am the parent or legal guardian and I consent to this account
                </label>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-teal-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </button>

          <p className="text-xs text-gray-500 dark:text-slate-400 text-center">
            Your date of birth is used for age-appropriate content and is protected under our{' '}
            <a href="/privacy" className="text-teal-600 dark:text-teal-400 hover:underline">Privacy Policy</a>.
          </p>
        </form>
      </div>
    </div>
  );
}
